import {
  Checkbox,
  FormInput,
  FormSelect,
  LocaleEnum,
  Popover,
  Tooltip,
  getLocaleLanguage,
  toast,
} from '@systemeio/ui'
import {
  AFFILIATE_PROGRAM_SETTINGS_API,
  createLink,
  deleteLink,
  deletePaypal,
  deleteWise,
  saveAffiliateProgram,
} from 'modules/profile/affiliate-program-settings/api/affiliate-program-settings-api'
import AffiliateProgramPaypal from 'modules/profile/affiliate-program-settings/components/affiliate-program-paypal'
import AffiliateProgramSettingsSkeleton from 'modules/profile/affiliate-program-settings/components/affiliate-program-settings-skeleton'
import FormAffiliateLink from 'modules/profile/affiliate-program-settings/components/form-affiliate-link'
import { AffiliateProgramSettingsLoco } from 'modules/profile/affiliate-program-settings/constants/affiliate-program-settings-loco'
import { useAffiliateProgramSettings } from 'modules/profile/affiliate-program-settings/hooks/use-affiliate-program-settings'
import {
  AffiliateProgramErrorsInterface,
  AffiliateProgramLinkCreateInterface,
  AffiliateProgramLinkErrorsInterface,
  AffiliateProgramLinksInterface,
  AffiliateProgramSettingsInterface,
} from 'modules/profile/affiliate-program-settings/types/affiliate-program-settings-interface'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUserDomains from 'shared/hooks/use-user-domains'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { LocaleDataInterface } from 'shared/types/locale-data-interface'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { isValidUrl } from 'shared/utils/is-valid-url'
import { BinIcon, QuestionMarkIcon } from '@systemeio/ui/icons'

const defaultValues: AffiliateProgramSettingsInterface = {
  defaultCommission: '0',
  defaultPayoutDelay: '0',
  marketplaceBrandName: '',
  minimumPayoutAmount: '0',
  secondTierCommission: '0',
  showLeadEmailForAffiliates: false,
}

const defaultErrors: AffiliateProgramErrorsInterface = {
  showLeadEmailForAffiliates: '',
  marketplaceBrandName: '',
  secondTierCommission: '',
  defaultPayoutDelay: '',
  defaultCommission: '',
  minimumPayoutAmount: '',
}

const defaultCreateLinkValues: AffiliateProgramLinkCreateInterface = {
  hostname: undefined,
  url: '',
  label: '',
  locale: undefined,
}

const defaultLinkErrors: AffiliateProgramLinkErrorsInterface = {
  locale: '',
  label: '',
  url: '',
  hostname: '',
}

const AffiliateProgramSettings: NextPage = () => {
  const { t } = useLocoTranslation()
  const { setPagination, pagination, setLimit, limit } = usePagination(true)
  const {
    affiliateProgram,
    mutateAffiliateProgram,
    affiliateProgramPayment,
    mutatePayment,
    affiliateProgramLinks,
  } = useAffiliateProgramSettings({
    limit,
    pagination,
  })
  const updateData = useUpdateData({
    setPagination,
    api: AFFILIATE_PROGRAM_SETTINGS_API,
    customApi: `${AFFILIATE_PROGRAM_SETTINGS_API}/links`,
    constantApi: [affiliateProgram, affiliateProgramPayment],
  })
  const [isFetching, setIsFetching] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const localeData: LocaleDataInterface[] = Object.values(LocaleEnum).map(locale => ({
    id: locale as LocaleEnum,
    caption: getLocaleLanguage(locale),
  }))
  const {
    userDomains,
    userDomainsSelect,
    getDomainIdByName,
    getDomainNameById,
    userDefaultDomain,
  } = useUserDomains(isCreateModalOpen)
  const [defaultLinkValues, setDefaultLinkValues] =
    useState<AffiliateProgramLinkCreateInterface>(defaultCreateLinkValues)
  useEffect(() => {
    if (userDefaultDomain) {
      setDefaultLinkValues(prev => ({ ...prev, hostname: userDefaultDomain.name }))
    }
  }, [userDomains, userDefaultDomain])
  useEffect(() => {
    setDefaultLinkValues(prev => ({ ...prev, locale: LocaleEnum.ENGLISH }))
  }, [])

  const [tempState, setTempState] = useState<AffiliateProgramSettingsInterface>(defaultValues)
  const [errors, setErrors] = useState<AffiliateProgramErrorsInterface>(defaultErrors)

  const [linkErrors, setLinkErrors] =
    useState<AffiliateProgramLinkErrorsInterface>(defaultLinkErrors)

  const [isRemoveWiseOpen, setIsRemoveWiseOpen] = useState(false)
  const [isRemovePaypalOpen, setIsRemovePaypalOpen] = useState(false)

  useEffect(() => {
    if (affiliateProgram) {
      setTempState({
        ...affiliateProgram,
      })
    }
  }, [affiliateProgram])

  const onWiseDelete = async () => {
    try {
      await deleteWise()
      await mutatePayment(data => {
        if (data) {
          return {
            ...data,
            user: { ...data.user, transferWiseAccount: null },
          }
        }
      }, false)
    } catch (e) {
      throw e
    }
  }

  const onPaypalDelete = async () => {
    try {
      await deletePaypal()
      await mutatePayment(data => {
        if (data) {
          return {
            ...data,
            user: { ...data.user, systemeioUser: { affiliateProgramPayPalCredentials: null } },
          }
        }
      }, false)
    } catch (e) {
      throw e
    }
  }

  const onPaypalAdd = async (clientId: string) => {
    await mutatePayment(data => {
      if (data) {
        return {
          ...data,
          user: {
            ...data.user,
            systemeioUser: { affiliateProgramPayPalCredentials: { clientId: clientId } },
          },
        }
      }
    }, false)
  }

  const onSave = async () => {
    await fetchWithErrors(async () => {
      setIsFetching(true)
      const response = await saveAffiliateProgram(tempState)
      setTempState(prev => ({ ...prev, ...response.data }))
      await mutateAffiliateProgram(response.data, false)
      setIsFetching(false)
      toast.success(t('dashboard.settings.affiliate_program.settings_modified'))
    }, setErrors)
  }

  const affiliateProgramLinksColumns: ColumnDefinitionInterface<AffiliateProgramLinksInterface>[] =
    [
      {
        key: 'hostname',
        header: '',
        widthPercent: 100,
        onRender: link => (
          <div className={'flex w-full flex-col gap-2'}>
            <div className={'flex items-center gap-1'}>
              {link.label}
              <Popover label={link.url}>
                {show => (
                  <QuestionMarkIcon
                    className={`${
                      show ? 'fill-brand-primary' : 'fill-fill-primary'
                    } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                  />
                )}
              </Popover>
            </div>
            <FormAffiliateLink
              value={`${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${link.hostname}/proxy/request/${link.hash}`}
            />
          </div>
        ),
      },
    ]

  const onLinkDelete = async (link: AffiliateProgramLinksInterface) => {
    await deleteLink(link.id)
    await updateData(false, affiliateProgramLinks)
  }

  const onLinkCreate = async (data: AffiliateProgramLinkCreateInterface) => {
    if (!isValidUrl(data.url)) {
      setLinkErrors(prev => ({
        ...prev,
        url: t('dashboard.settings.affiliate_program.invalid_url'),
      }))
      throw new Error()
    }
    await fetchWithErrors(async () => {
      await createLink(data)
      await updateData(true, affiliateProgramLinks)
    }, setLinkErrors)
  }

  const handlePercent = (value: string) => Math.max(0, Math.min(100, Number(value))).toString()
  const handleAmount = (value: string) => Math.max(0, Math.min(1000000, Number(value))).toString()

  return (
    <SettingsLayout
      titles={[{ caption: t(settingsMenuLocoKeys.affiliate_program), isStatic: true }]}
      isFetching={isFetching}
      onSave={onSave}
    >
      <div className={'flex w-full flex-col gap-5'}>
        {affiliateProgram && affiliateProgramPayment ? (
          <form className="bg-background-secondary [&>*]:flex-1 flex h-fit w-full flex-col gap-7 overflow-x-hidden rounded-md p-5">
            <div className={'flex flex-col gap-2'}>
              {affiliateProgramPayment.user.transferWiseAccount ? (
                <div className={'flex gap-1'}>
                  {`${t('dashboard.settings.affiliate_program.wise_account')}:`}
                  <Tooltip
                    mode={'overflow'}
                    label={affiliateProgramPayment.user.transferWiseAccount.profileName}
                  >
                    <span className={'text-green max-w-[200px] truncate'}>
                      {affiliateProgramPayment.user.transferWiseAccount.profileName}
                    </span>
                  </Tooltip>
                  <Tooltip
                    mode={'hover'}
                    label={t('dashboard.settings.affiliate_program.wise_remove_tooltip')}
                  >
                    <button
                      className={'group outline-hidden'}
                      onClick={e => {
                        e.preventDefault()
                        setIsRemoveWiseOpen(true)
                      }}
                    >
                      <BinIcon
                        className={
                          'group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]'
                        }
                      />
                      <ConfirmationModal
                        confirmationText={t('dashboard.settings.affiliate_program.wise_remove')}
                        opened={isRemoveWiseOpen}
                        onClose={() => setIsRemoveWiseOpen(false)}
                        onConfirm={onWiseDelete}
                      />
                    </button>
                  </Tooltip>
                </div>
              ) : (
                <div className={'flex gap-1'}>
                  <span>{t('dashboard.settings.affiliate_program.wise_not_connected')}</span>
                  <a
                    className={'primary-link'}
                    href={`https://${
                      process.env.NEXT_PUBLIC_APP_ENV === 'development' ||
                      process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                        ? 'sandbox.transferwise.tech'
                        : 'wise.com'
                    }/oauth/authorize/?client_id=systemeio&redirect_uri=${
                      window.location.origin
                    }/dashboard/profile/transfer-wise/account/connect`}
                    target={'_blank'}
                    rel={'noreferrer'}
                  >
                    {t('dashboard.settings.affiliate_program.connect')}
                  </a>
                </div>
              )}
              {affiliateProgramPayment.user.systemeioUser.affiliateProgramPayPalCredentials ? (
                <div className={'flex gap-1'}>
                  {`${t('dashboard.settings.affiliate_program.paypal_connected')}:`}
                  <Tooltip
                    mode={'overflow'}
                    label={
                      affiliateProgramPayment.user.systemeioUser.affiliateProgramPayPalCredentials
                        .clientId
                    }
                  >
                    <span className={'text-green max-w-[200px] truncate'}>
                      {
                        affiliateProgramPayment.user.systemeioUser.affiliateProgramPayPalCredentials
                          .clientId
                      }
                    </span>
                  </Tooltip>
                  <Tooltip
                    mode={'hover'}
                    label={t('dashboard.settings.affiliate_program.paypal_remove_tooltip')}
                  >
                    <button
                      className={'group outline-hidden'}
                      onClick={e => {
                        e.preventDefault()
                        setIsRemovePaypalOpen(true)
                      }}
                    >
                      <BinIcon
                        className={
                          'group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]'
                        }
                      />
                      <ConfirmationModal
                        confirmationText={t('dashboard.settings.affiliate_program.paypal_remove')}
                        opened={isRemovePaypalOpen}
                        onClose={() => setIsRemovePaypalOpen(false)}
                        onConfirm={onPaypalDelete}
                      />
                    </button>
                  </Tooltip>
                </div>
              ) : affiliateProgramPayment.isPaypalIntegrationAvailable ? (
                <div className={'flex gap-1'}>
                  <span>{t('dashboard.settings.affiliate_program.paypal_not_connected')}</span>
                  <AffiliateProgramPaypal onPaypalAdd={onPaypalAdd} />
                </div>
              ) : (
                <span>{t('dashboard.settings.affiliate_program.paypal_forbidden')}</span>
              )}
            </div>
            <div className={'[&>*]:flex-1 flex w-full flex-col gap-7 xl:flex-row xl:items-end'}>
              <FormInput
                value={tempState.defaultCommission}
                type={'number'}
                max={'100'}
                min={'0'}
                onChange={e =>
                  setTempState(prev => ({
                    ...prev,
                    defaultCommission: handlePercent(e.target.value),
                  }))
                }
                label={t('dashboard.settings.affiliate_program.default_commission')}
                error={errors.defaultCommission}
              />
              <FormInput
                value={tempState.secondTierCommission}
                type={'number'}
                onChange={e =>
                  setTempState(prev => ({
                    ...prev,
                    secondTierCommission: handlePercent(e.target.value),
                  }))
                }
                label={t('dashboard.settings.affiliate_program.second_affiliate_commission')}
                error={errors.secondTierCommission}
              />
            </div>
            <div className={'[&>*]:flex-1 flex w-full flex-col gap-7 xl:flex-row xl:items-end'}>
              <FormInput
                value={tempState.minimumPayoutAmount}
                type={'number'}
                onChange={e =>
                  setTempState(prev => ({
                    ...prev,
                    minimumPayoutAmount: handleAmount(e.target.value),
                  }))
                }
                label={t('dashboard.settings.affiliate_program.minimum_payout_amount')}
                error={errors.minimumPayoutAmount}
              />
              <FormInput
                value={tempState.defaultPayoutDelay}
                type={'number'}
                onChange={e =>
                  setTempState(prev => ({
                    ...prev,
                    defaultPayoutDelay: handlePercent(e.target.value),
                  }))
                }
                label={t('dashboard.settings.affiliate_program.payout_delay')}
                error={errors.defaultPayoutDelay}
              />
            </div>
            <FormInput
              value={tempState.marketplaceBrandName}
              onChange={e =>
                setTempState(prev => ({ ...prev, marketplaceBrandName: e.target.value }))
              }
              label={
                <div className={'flex items-center gap-1'}>
                  {t('dashboard.settings.affiliate_program.vendor_name')}{' '}
                  <Popover
                    label={t('dashboard.settings.affiliate_program.vendor_name_description')}
                  >
                    {show => (
                      <QuestionMarkIcon
                        className={`${
                          show ? 'fill-brand-primary' : 'fill-fill-primary'
                        } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                      />
                    )}
                  </Popover>
                </div>
              }
              error={errors.marketplaceBrandName}
            />
            <Checkbox
              label={t('dashboard.settings.affiliate_program.allow_affilaite_view_email')}
              value={tempState.showLeadEmailForAffiliates}
              onChange={value =>
                setTempState(prev => ({ ...prev, showLeadEmailForAffiliates: value }))
              }
            />
          </form>
        ) : (
          <AffiliateProgramSettingsSkeleton />
        )}
        <div className={'w-full'}>
          <ListingLayout
            counter={false}
            createData={{
              caption: t('dashboard.settings.affiliate_program.proxy_link'),
              defaultValues: defaultLinkValues,
              onOpen: async () => setIsCreateModalOpen(true),
              requiredFields: ['locale', 'label', 'url', 'hostname'],
              onAfterLeave: () => setLinkErrors(defaultLinkErrors),
              creatable: [
                {
                  field: 'label',
                  onRender: (value, onChange) => (
                    <FormInput
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      label={t('global.label')}
                      required
                      key={'label-create'}
                      error={linkErrors.label}
                    />
                  ),
                },
                {
                  field: 'locale',
                  onRender: (value, onChange) => (
                    <FormSelect
                      value={value}
                      data={localeData}
                      onChange={data => onChange(data)}
                      label={t('global.language')}
                      required
                      key={'locale-create'}
                      error={linkErrors.locale}
                    />
                  ),
                },
                {
                  field: 'url',
                  onRender: (value, onChange) => (
                    <FormInput
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      label={t('global.url')}
                      required
                      key={'url-create'}
                      error={linkErrors.url}
                    />
                  ),
                },
                {
                  field: 'hostname',
                  onRender: (value, onChange) => (
                    <FormSelect
                      required
                      isPreFetching={!userDomainsSelect}
                      key="hostname-create"
                      withoutCloseIcon
                      onChange={value => value && onChange(getDomainNameById(value))}
                      value={getDomainIdByName(value)}
                      label={t('global.domain')}
                      data={userDomainsSelect}
                      error={linkErrors.hostname}
                    />
                  ),
                },
              ],
              onCreate: data => onLinkCreate(data),
            }}
            data={affiliateProgramLinks}
            title={
              <h1 className={'flex items-center gap-1.5'}>
                {t('dashboard.settings.affiliate_program.proxy_links')}
                <Popover
                  label={t('dashboard.settings.affiliate_program.proxy_links_description-2')}
                >
                  {show => (
                    <QuestionMarkIcon
                      className={`${
                        show ? 'fill-brand-primary' : 'fill-fill-primary'
                      } group-focus-visible:fill-brand-primary`}
                    />
                  )}
                </Popover>
              </h1>
            }
            type={'compact'}
          >
            <Table
              data={affiliateProgramLinks}
              columns={affiliateProgramLinksColumns}
              actions={{ remove: { onChange: link => onLinkDelete(link) } }}
              caption={t('dashboard.settings.affiliate_program.proxy_link')}
              limiter={{ limit, setLimit }}
              paginator={{ pagination, setPagination }}
              noHeader
            />
          </ListingLayout>
        </div>
      </div>
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  AffiliateProgramSettingsLoco,
  'global.settings',
)

export default AffiliateProgramSettings
